import { Box, Grid, Skeleton, Text } from '@chakra-ui/react'
import { OperationType, StatusView } from './StatusView'
import { mipdStore, thirdwebClient } from '@/services/wallet-service'
import { createWallet, EIP1193, WalletId } from 'thirdweb/wallets'
import { useSignInModal } from '../SigninHostModal'
import { useState } from 'react'
import { getAllWalletsList } from 'thirdweb/wallets'
import { useQuery } from '@tanstack/react-query'
import { StageTitle } from './StageTitle'
import { WalletImage } from './WalletImage'
import { isMobile } from '@/helpers/device'
import { SearchBar } from '@/components/atoms/SearchBar/SearchBar'
import { useTranslation } from 'next-i18next/pages'

export const WalletsView = () => {
  const [isProcessing, setProcessing] = useState(false)
  const [error, setError] = useState(false)
  const { handleWalletFlow, navigateTo } = useSignInModal()
  const [walletId, setWalletId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const { t } = useTranslation()

  const handleConnectWallet = async (walletId: string) => {
    setWalletId(walletId)

    try {
      const timeoutId = setTimeout(() => {
        setProcessing(true)
      }, 50)

      let mipdWallet = walletId
      const provider = mipdStore.findProvider({ rdns: mipdWallet as WalletId })
      const wallet =
        provider != undefined
          ? EIP1193.fromProvider({
              provider: provider!.provider,
              walletId: mipdWallet as any,
            })
          : createWallet(walletId as WalletId)

      setWalletId(wallet.id)

      const account = await wallet.connect({
        client: thirdwebClient,
        walletConnect: {
          projectId: process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID,
          showQrModal: !isMobile(),
          qrModalOptions: { themeVariables: { '--wcm-z-index': '9999999' }, enableExplorer: false },
        },
      })

      clearTimeout(timeoutId)

      handleWalletFlow(wallet.id, (account as any).address)
    } catch {
      setError(true)
    }
  }

  if (isProcessing) {
    return (
      <StatusView
        error={error}
        isSuccess={false}
        backCallback={() => {
          setError(false)
          setProcessing(false)
        }}
        retryCallback={() => {
          setError(false)
          handleConnectWallet(walletId || '')
        }}
        provider={walletId}
        operationType={OperationType.WalletRequest}
      />
    )
  }

  return (
    <>
      <StageTitle backCallback={() => navigateTo('SignIn')} title={t('signin.wallets')} />
      <Box flexShrink={0}>
        <SearchBar placeholder={t('signin.search-wallet')} setSearch={setSearch} search={search} />
      </Box>
      <Box
        h={{ base: '333px', lg: 'full' }}
        pos={'relative'}
        overflowY={'auto'}
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#15143d',
            borderRadius: '24px',
          },
        }}
      >
        <Box pt={3} pos={'absolute'} top={0} right={0} left={0}>
          <WalletsList search={search} handleConnectWallet={handleConnectWallet} />
        </Box>
      </Box>
    </>
  )
}

const walletsPriorities = {
  'io.metamask': 10,
  'app.keplr': 10,
  'app.phantom': 10,
  walletConnect: 10,
  'com.coinbase.wallet': 10,
}

const blackListedWallets = ['inApp', 'embedded']

const WalletsList = ({
  search,
  handleConnectWallet,
}: {
  search: string
  handleConnectWallet: (walletId: string) => Promise<void>
}) => {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['ALL_WALLETS'],
    queryFn: async () => getAllWalletsList(),
  })

  if (isLoading) {
    return Array.from({ length: 10 }, (_, k) => (
      <Skeleton
        key={k}
        mb={2}
        height={'56px'}
        w={'full'}
        rounded={'xl'}
        startColor={'ninja.500'}
        endColor={'ninja.400'}
      />
    ))
  }

  return (
    <>
      {data
        ?.filter((wallet) => !blackListedWallets.includes(wallet.id))
        ?.filter((wallet) => wallet.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => {
          const priorityA = walletsPriorities[a.id as keyof typeof walletsPriorities] || 0
          const priorityB = walletsPriorities[b.id as keyof typeof walletsPriorities] || 0

          const aInstalledBonus = !!mipdStore.findProvider({ rdns: a.id }) ? 10 : 0
          const bInstalledBonus = !!mipdStore.findProvider({ rdns: b.id }) ? 10 : 0

          return priorityB + bInstalledBonus - (priorityA + aInstalledBonus)
        })
        ?.map((wallet, index) => (
          <Grid
            key={wallet.id + index}
            onClick={() => {
              handleConnectWallet(wallet.id!)
            }}
            mb={2}
            p={2}
            rounded={'xl'}
            cursor={'pointer'}
            _hover={{
              bg: 'ninja.500',
            }}
            alignItems={'center'}
            gap={3}
            gridTemplateColumns={'40px auto'}
          >
            <WalletImage id={wallet.id} />
            <Box fontWeight={700}>
              <Text lineHeight={1} fontSize={'xl'}>
                {wallet.name}
              </Text>
              {mipdStore.findProvider({ rdns: wallet.id }) && (
                <Text fontSize={'sm'} color={'ninja.300'}>
                  {t('wallet.installed')}
                </Text>
              )}
            </Box>
          </Grid>
        ))}
    </>
  )
}
